import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ChevronRight, Cloud, Download, LogIn, LogOut, RefreshCw, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { replayLenient } from '@airmail/engine';
import { useAuth } from '@/store/AuthProvider';
import { usePlan } from '@/store/plan';
import { db, deleteTournament, saveTournament, setzeOhneCloud } from '@/store/db';
import { DEFAULT_API, deleteCloudTournament, joinOwnTournament, listCloudTournaments, loadSync, removeSync, type CloudTournament } from '@/store/sync';
import { Button, Pill, cx } from '@/ui';
import { choiceDialog, confirmDialog } from '@/ui/dialogs';
import { fmtDate } from '@/lib/format';
import { filterEintrag, statusLabel, type Filter, type TurnierStatus } from '@/lib/turnierfilter';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';

/** Kopfzeile: Konto-Status (docs/16) */
export function AccountMenu() {
  const auth = useAuth();
  const t = useT();
  // Menüpunkt „Admin" nur für Betreiber-Konten (docs/21 §5) — der Server prüft es ohnehin noch einmal
  const admin = !!usePlan().konto?.admin;
  if (!auth.available) return null;
  if (auth.loading) return <span className="text-xs text-dim">…</span>;
  if (!auth.user) {
    return (
      <Button onClick={() => void auth.login('/')} title={t('Organisatoren-Konto: Turniere in der Cloud, Helfer-Geräte, Online-Anmeldung')}>
        <LogIn size={14} /> {t('Anmelden')}
      </Button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted">
      <Link to="/konto" className="no-underline text-muted hover:text-ink inline-flex items-center gap-1.5" title={t('Konto')} aria-label={t('Konto')}>
        <UserRound size={14} />
        <span className="hidden sm:inline truncate max-w-[220px]">{auth.user.email ?? auth.user.sub}</span>
      </Link>
      {admin && (
        <Link to="/admin" className="no-underline text-blue hover:text-ink inline-flex items-center gap-1" title={t('Admin — Konten und Stufen')} data-admin-link>
          <ShieldCheck size={14} />
          <span className="hidden sm:inline">{t('Admin')}</span>
        </Link>
      )}
      <Button size="sm" variant="ghost" onClick={() => void auth.logout()} title={t('Abmelden')} aria-label={t('Abmelden')}>
        <LogOut size={14} />
      </Button>
    </span>
  );
}

/** Ein-/ausgeklappt merkt sich der Browser — `zu` heißt zu, alles andere (auch nichts) offen */
const CLOUD_KEY = 'airmail.cloudList';
function gemerktOffen(): boolean {
  try {
    return localStorage.getItem(CLOUD_KEY) !== 'zu';
  } catch {
    return true;
  }
}

/**
 * Turnierliste: „In der Cloud" — eigene Turniere des Kontos, auch von anderen
 * Geräten. Der Statusfilter der Seite gilt auch hier (`filter`); für Turniere,
 * die auch auf dem Gerät liegen, zählt der lokale Stand (`lokal`), für die
 * anderen der vom Server (kann bei alten Turnieren fehlen → nur unter „Alle“).
 * `onList` meldet die geladene Liste nach oben, damit die Zahlen im
 * Wahlschalter die Cloud-Turniere mitzählen, die nicht auf dem Gerät sind.
 */
export function CloudList({
  lokal,
  filter,
  reloadKey,
  onChanged,
  onList,
}: {
  lokal: Map<string, TurnierStatus>;
  filter: Filter;
  reloadKey: string;
  onChanged: () => void;
  onList?: (list: CloudTournament[] | null) => void;
}) {
  const auth = useAuth();
  const nav = useNavigate();
  const t = useT();
  const tm = useTm();
  const [list, setList] = useState<CloudTournament[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [offen, setOffenState] = useState<boolean>(gemerktOffen);
  const api = DEFAULT_API;
  // über eine Ref, damit ein neu gebauter Callback des Elternteils nicht jedes Mal neu lädt
  const onListRef = useRef(onList);
  onListRef.current = onList;

  const load = useCallback(async () => {
    if (!auth.user || !api) return;
    try {
      const l = await listCloudTournaments(api);
      setList(l);
      onListRef.current?.(l);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [auth.user, api]);

  // neu laden, wenn sich lokal etwas geändert hat (z. B. ein Turnier wurde gerade automatisch gesichert).
  // Auch zugeklappt — die Zahlen in der Kopfzeile und im Filter brauchen die Liste.
  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  if (!auth.available || !auth.user || !api) return null;

  const setOffen = (o: boolean) => {
    setOffenState(o);
    try {
      if (o) localStorage.removeItem(CLOUD_KEY);
      else localStorage.setItem(CLOUD_KEY, 'zu');
    } catch {
      /* egal */
    }
  };
  const statusVon = (ct: CloudTournament): TurnierStatus | undefined => lokal.get(ct.id) ?? ct.status;
  const aktiv = filterEintrag(filter);
  const sichtbar = (list ?? []).filter((ct) => aktiv.trifft(statusVon(ct)));
  const nichtHier = (list ?? []).filter((ct) => !lokal.has(ct.id)).length;

  const fetchToDevice = async (t: CloudTournament) => {
    setBusy(t.id);
    try {
      const { cfg, init } = await joinOwnTournament(api, t.id);
      const { state } = replayLenient(
        init,
        cfg.remote.map((e) => e.action),
      );
      await saveTournament(state);
      onChanged();
      nav(`/t/${t.id}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /*
   * Löschen aus der Cloud. Liegt das Turnier auch auf dem Gerät, wird nach der
   * lokalen Kopie gefragt: „Beides löschen" oder „Nur aus der Cloud". Im zweiten
   * Fall bekommt die lokale Kopie „nicht in der Cloud" (store/db.ts) — vorher
   * lud die automatische Sicherung sie im selben Augenblick wieder hoch, und das
   * Turnier stand sofort wieder in der Liste.
   */
  const remove = async (ct: CloudTournament) => {
    const local = lokal.has(ct.id);
    let auchLokal = false;
    if (local) {
      const wahl = await choiceDialog(
        t('„{name}“ aus der Cloud löschen? Protokoll und Anmeldungen auf dem Server sind dann weg. Was soll mit der Kopie auf diesem Gerät geschehen?', { name: ct.name }),
        {
          wahl: [
            { key: 'cloud', label: t('Nur aus der Cloud') },
            { key: 'beides', label: t('Beides löschen'), danger: true },
          ],
        },
      );
      if (!wahl) return;
      auchLokal = wahl === 'beides';
    } else if (
      !(await confirmDialog(t('„{name}“ aus der Cloud löschen? Protokoll und Anmeldungen auf dem Server sind dann weg.', { name: ct.name }), {
        ok: t('Aus der Cloud löschen'),
        danger: true,
      }))
    )
      return;
    setBusy(ct.id);
    let gesperrt = false;
    try {
      // Erst die Sperre, dann der Server: Käme dazwischen ein Abgleich, wäre das Turnier sonst schon wieder oben
      if (local && !auchLokal) {
        await setzeOhneCloud(ct.id, true);
        gesperrt = true;
      }
      await deleteCloudTournament(api, ct.id);
      gesperrt = false;
      if (await loadSync(ct.id)) await removeSync(ct.id);
      if (auchLokal) await deleteTournament(ct.id);
      await load();
      onChanged();
    } catch (e) {
      // Der Server hat nicht gelöscht: Dann bleibt das Turnier in der Cloud — und die Sperre wäre falsch
      if (gesperrt) await setzeOhneCloud(ct.id, false).catch(() => undefined);
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-8" data-cloud-list={offen ? 'offen' : 'zu'}>
      {/* Kopfzeile ist der Schalter: Chevron + Titel + Zahl, am Handy 44 px hoch; die Zahl steht auch zugeklappt */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="text-[15px] font-bold tracking-tight m-0 min-w-0">
          <button
            type="button"
            onClick={() => setOffen(!offen)}
            aria-expanded={offen}
            aria-controls="cloud-liste"
            title={t('Ein- oder ausklappen')}
            data-cloud-toggle
            className="inline-flex items-center gap-2 max-w-full bg-transparent border-0 p-0 -ml-1 pl-1 text-ink font-bold cursor-pointer max-md:min-h-11 hover:text-red"
          >
            <ChevronRight size={16} className={cx('shrink-0 transition-transform', offen && 'rotate-90')} aria-hidden />
            <Cloud size={14} className="shrink-0" aria-hidden />
            {/* Titel bricht nie, die Zahl dahinter darf am Handy umbrechen */}
            <span className="whitespace-nowrap shrink-0">{t('In der Cloud')}</span>
            {list && (
              <span className="font-mono text-[11px] text-dim font-normal tracking-normal text-left" data-cloud-zahl>
                · {list.length}
                {nichtHier > 0 && `, ${t('davon {m} nicht auf diesem Gerät', { m: nichtHier })}`}
              </span>
            )}
          </button>
        </h2>
        <span className="font-mono text-[11px] text-dim hidden sm:inline truncate">{auth.user.email}</span>
        {offen && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => void load()}>
            <RefreshCw size={12} /> {t('Aktualisieren')}
          </Button>
        )}
      </div>
      {error && <div className="text-err text-xs mb-2">{tm(error)}</div>}
      {/* Zugeklappt wird die Liste nicht gezeichnet — geladen ist sie trotzdem (Zahlen oben) */}
      {offen && (
        <div id="cloud-liste">
          {list === null && !error && <div className="text-dim text-sm">{t('Lade …')}</div>}
          {list && list.length === 0 && <div className="text-dim text-sm">{t('Noch kein Turnier in der Cloud. Ein Turnier kommt dorthin, sobald du in den Einstellungen Cloud-Sync aktivierst.')}</div>}
          {list && list.length > 0 && sichtbar.length === 0 && (
            <div className="text-dim text-sm" data-cloud-leer>
              {t(aktiv.leerCloud ?? 'Noch kein Turnier')}
            </div>
          )}
          {sichtbar.length > 0 && (
            <ul className="m-0 p-0 list-none grid gap-2">
              {sichtbar.map((ct) => {
                const local = lokal.has(ct.id);
                const status = statusVon(ct);
                return (
                  /* Wie die Liste oben: am Handy rutschen Pille und Knöpfe unter den Namen */
                  <li key={ct.id} className="border border-line flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-sm" data-cloud-turnier={ct.id} data-cloud-status={status ?? 'unbekannt'}>
                    <div className="flex-1 min-w-[12rem]">
                      <div className="font-bold tracking-tight truncate">{ct.name}</div>
                      <div className="font-mono text-[11px] text-dim">
                        {fmtDate(ct.date) || t('ohne Datum')} · {t('{n} Aktionen', { n: ct.seq })} · {status ? t(statusLabel(status)) : t('Stand unbekannt')}
                        {ct.registrationOpen ? ` · ${t('Anmeldung offen')}` : ''}
                        {/* Nur im Konto — etwa nach „Nur auf diesem Gerät löschen": bleibt hier, bis es jemand ausdrücklich holt */}
                        {!local && <span data-cloud-nur> · {t('nicht auf diesem Gerät')}</span>}
                        {ct.role === 'member' && <span className="text-blue"> · {t('geteilt von {who}', { who: ct.ownerEmail ?? t('anderem Konto') })}</span>}
                      </div>
                    </div>
                    <span className="ml-auto flex items-center gap-3">
                      {local ? (
                        <Pill tone="live">{t('auf diesem Gerät')}</Pill>
                      ) : (
                        <Button size="sm" onClick={() => void fetchToDevice(ct)} disabled={busy === ct.id}>
                          <Download size={12} /> {t('Auf dieses Gerät holen')}
                        </Button>
                      )}
                      {ct.role === 'owner' && (
                        <Button variant="ghost" size="sm" title={t('Aus der Cloud löschen')} onClick={() => void remove(ct)} disabled={busy === ct.id}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/** Hilfsfunktion für die Liste: existiert das Turnier lokal? */
export async function localTournamentIds(): Promise<Set<string>> {
  const keys = await db.tournaments.toCollection().primaryKeys();
  return new Set(keys as string[]);
}
